import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { CardContainer } from "@msys/ui";
import { Link, Stack, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import { namedOperations } from "../../../clients/graphqlTypes.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageGrid } from "../../commons/layout/PageGrid.js";
import { Attachment } from "../../features/attachments/helpers.js";
import { CrmPersonCustomFieldsBox } from "../../features/crm-persons/boxes/CrmPersonCustomFieldsBox.js";
import { CrmPersonPendingContracteeInvitationsBox } from "../../features/crm-persons/boxes/CrmPersonPendingContracteeInvitationsBox.js";
import { CrmPersonInformationData } from "../../features/crm-persons/CrmPersonInformationData.js";
import { CrmPersonInformationForm } from "../../features/crm-persons/CrmPersonInformationForm.js";
import { CrmEmailMessagesBox } from "../../features/crm/CrmEmailMessagesBox.js";
import { TodosBox } from "../../features/todos/TodosBox.js";
import { UserAvatar } from "../../features/users/UserAvatar.js";
import {
  useCrmPersonProfileQuery,
  useModifyCrmUserAvatarMutation,
} from "./CrmPersonProfile.generated.js";
import {
  CrmPersonAvatarAndTitleData,
  CrmPersonAvatarAndTitleForm,
} from "../../features/crm-persons/CrmPersonAvatarAndTitleForm.js";

interface Props {
  submenuItems: PageTopbarItem[];
}

export function CrmPersonProfile({ submenuItems }: Props) {
  const { userId, organisationId } = useParams();
  if (!userId) throw new Error("User id is missing");

  const client = useApolloClient();
  const query = useCrmPersonProfileQuery({
    client,
    variables: { userId },
  });

  const crmPerson = getDataOrNull(query.data?.crmPerson)?.crmPerson;

  if (crmPerson && !organisationId) {
    // Redirecting to the crm user's profile specific to the crm organisation
    return (
      <Navigate
        to={`/crm/organisations/${crmPerson.crmCompany.id}/users/${crmPerson.id}`}
        replace
      />
    );
  }

  return (
    <Page
      title={crmPerson ? `${crmPerson.firstname} ${crmPerson.familyname}` : ""}
      submenuItems={submenuItems}
    >
      {crmPerson && (
        <PageContainer>
          <PageGrid columns={{ xs: 1, md: 2, xl: 4 }}>
            <>
              <CardContainer>
                <Stack
                  direction="column"
                  spacing={1}
                  sx={{
                    p: 1,
                  }}
                >
                  <RestrictedByOrganisationPermissionWithDebug
                    permission="MANAGE_CRM"
                    otherwise={
                      <CrmPersonAvatarAndTitleData crmPerson={crmPerson} />
                    }
                  >
                    <CrmPersonAvatarAndTitleForm crmPerson={crmPerson} />
                  </RestrictedByOrganisationPermissionWithDebug>

                  <RestrictedByOrganisationPermissionWithDebug
                    permission="MANAGE_CRM"
                    otherwise={
                      <CrmPersonInformationData crmPerson={crmPerson} />
                    }
                  >
                    <CrmPersonInformationForm crmPerson={crmPerson} />
                  </RestrictedByOrganisationPermissionWithDebug>
                </Stack>
              </CardContainer>

              <CrmPersonCustomFieldsBox
                crmPerson={crmPerson}
                onChangeCustomFieldsRefetchQueries={[
                  namedOperations.Query.CrmPersonProfile,
                ]}
              />
            </>
            <>
              <TodosBox linkedCrmUser={crmPerson} />
              <CrmEmailMessagesBox
                crmPerson={crmPerson}
                refetchQueries={[namedOperations.Query.CrmPersonProfile]}
              />
            </>
            <>
              <CrmPersonPendingContracteeInvitationsBox userId={crmPerson.id} />
            </>
          </PageGrid>
        </PageContainer>
      )}
    </Page>
  );
}
